import { Upload, Input, Form, Button, Popover, Switch, FormInstance } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ArrowIcon from '../../../assets/images/catalogue/arrowIcon';
import EditIcon from '../../../assets/images/catalogue/EditIcon';
import DescriptionSkeleton from '../../../components/formLoaders/descriptionSkeleton/Description';
import UploadButton from '../../../components/uploadButton';
import { activateOrDeactivateProducts, createImage, createURLFile, getProductDetail } from '../../../services/cataloge';
import { ICreateProductResponse, IProductDetailId } from '../../../types/products';
import Tabs from './tabs';
import fileIcon from '../../../assets/images/catalogue/fileIcon.svg';
import { LoadingOutlined } from '@ant-design/icons';
import MinUpLoadImg from '../../../components/minUploadImg';
import DeleteFile from '../../../assets/images/catalogue/deleteFile';
import FloatButtons from '../../../components/floatButtons';
import { truncateText } from '../../../utils/truncateText';
import { ICountryExchangeDetail } from '../../../types/countries';
import { getCountriesOpenForSelling } from '../../../services/countriesList';
import ActiveOrInactiveConfirmModal from '../../catalogue/Components/ActiveOrInactiveConfirmModal';
import { displayErrorNotification, displaySuccessNotification } from '../../../utils/displayNudosStandardNotifications';
import { SWR_KEYS } from '../../../constants/constants';
import useSWRMutation from 'swr/mutation';
import useCatalogueState from '../../../state/useCatalogueState';
import { NudosTextInput } from '../../../components/NudosComponents';
import urlParameter from '../../../utils/urlParameter';

interface ITypeProduct {
  id: number;
  type: string;
}
interface ISelectTab {
  title: string;
  number: number;
  view: boolean;
}

export interface IPendingStoreStatusChange {
  countryCode: string;
  isActive: boolean;
  isQuoteProduct: boolean;
}

interface IProductForm {
  typeProduct: ITypeProduct;
  productDataDetail?: IProductDetailId;
  loadingProduct: boolean;
  updateProduct: (dataForm: IProductDetailId, pendingStoreStatusChanges?: IPendingStoreStatusChange[]) => Promise<void>;
  createProduct: (dataForm: IProductDetailId) => Promise<ICreateProductResponse>;
  changeTab: (dataTab: ISelectTab) => void;
  selectTab: ISelectTab;
  setProductDataDetail: (data: IProductDetailId) => void;
  getDataOfTheCreatedProduct?: (productId?: number) => void;
}

const ProductForm: FC<IProductForm> = ({
  typeProduct,
  productDataDetail,
  loadingProduct,
  updateProduct,
  createProduct,
  changeTab,
  selectTab,
  setProductDataDetail,
  getDataOfTheCreatedProduct
}: IProductForm) => {
  const { type } = typeProduct;
  const { goBack, push } = useHistory();
  const sendToCatalogParam = urlParameter('sendToCatalog');
  const sendToCatalog = sendToCatalogParam === 'true';
  const [form] = Form.useForm<FormInstance>();
  const { showPricingForm, setShowPricingForm } = useCatalogueState();
  const [countriesOpenForSale, setCountriesOpenForSale] = useState<ICountryExchangeDetail[]>();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imgId, setImgId] = useState<number>();
  const [verifyGeneralInfo, setVerifyGeneralInfo] = useState<boolean>(false);
  const [verifySpecs, setVerifySpecs] = useState<boolean>(false);
  const [verifyPricing, setVerifyPricing] = useState<boolean>(false);
  const [updateInfo, setUpdateInfo] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [loadingSheet, setLoadingSheet] = useState<boolean>(false);
  const [firstImg, setFirstImg] = useState<string>();
  const [firstId, setFirstId] = useState<number | null>();
  const [firsLoading, setFirstLoading] = useState<boolean>(false);
  const [secondImg, setSecondImg] = useState<string>();
  const [secondId, setSecondId] = useState<number | null>();
  const [secondLoading, setSecondLoading] = useState<boolean>(false);
  const [thirdImg, setThirdImg] = useState<string>();
  const [thirdId, setThirdId] = useState<number | null>();
  const [thirdLoading, setThirdLoading] = useState<boolean>(false);
  const [changePricing, setChangePricing] = useState<boolean>(false);
  const [isKit, setIsKit] = useState<boolean>(false);
  const [, forceUpdate] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<number[]>([]);
  const [pendingStoreStatusChanges, setPendingStoreStatusChanges] = useState<IPendingStoreStatusChange[]>([]);

  /**
   * Registra el estado en tienda deseado para un país que se está agregando o cuyo estado
   * cambió en sesión. El último cambio por país gana (dedupe por countryCode).
   * Estos cambios se aplican vía PATCH /store-status tras el PUT del Guardar principal.
   */
  const addPendingStoreStatusChange = (change: IPendingStoreStatusChange) => {
    setPendingStoreStatusChanges(prev => [
      ...prev.filter(c => c.countryCode !== change.countryCode),
      change
    ]);
  };

  const { trigger: activateOrDeactivateProductsTrigger, isMutating: isLoadingActivateOrDeactivateProducts } =
    useSWRMutation(
      SWR_KEYS.activeOrdeactiveProducts,
      async (
        __,
        {
          arg
        }: {
          arg: {
            isActivate: boolean;
            body: number[];
          };
        }
      ) => {
        const response = await activateOrDeactivateProducts(arg);
        return response;
      },
      {
        onSuccess: async () => {
          const dataUpdated = await getProductDetail(productDataDetail?.productId);
          setProductDataDetail(dataUpdated);
          setShowConfirmModal(false);
          displaySuccessNotification({
            customJSXMessage: <>{renderActionButtonText}</>
          });
        }
      }
    );

  const countriesInfoObject = productDataDetail?.pricingData.reduce<{ [key: string]: any }>(
    (previousValue, currentValue) => {
      return { ...previousValue, [`${currentValue?.nameCode}`]: currentValue };
    },
    {}
  );

  const countryCodesOpenForSale = countriesOpenForSale
    ? countriesOpenForSale.reduce<string[]>((previousVal: string[], currentVal: ICountryExchangeDetail) => {
        const newValue = [...previousVal];
        if (typeof currentVal.code === 'string') {
          newValue.push(currentVal.code);
        }
        return newValue;
      }, [])
    : [];
  const productHasValidCountryPricingData = !!countriesInfoObject && Object.keys(countriesInfoObject)?.length > 0;
  const countries = productHasValidCountryPricingData ? Object.keys(countriesInfoObject) : countryCodesOpenForSale;
  const renderActionButtonText = productDataDetail?.isActive
    ? 'El SKU se inactivó correctamente'
    : 'El SKU se activó correctamente';

  const renderMarginTop = !productDataDetail?.isActive && productDataDetail?.productId ? 'mt-56' : '';

  const verifyFile = (file: File | any, showMessages?: boolean) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isJpgOrPng) {
      showMessages && displayErrorNotification({ customJSXMessage: <>solo puedes subir archivos JPG/PNG!</> });
      return true;
    } else if (!isLt2M) {
      showMessages && displayErrorNotification({ customJSXMessage: <>La imagen debe pesar menos de  2MB!</> });
      return true;
    }
    return false;
  };

  const beforeUpload = (file: File) => {
    const checkFile = verifyFile(file);
    if (file && !checkFile) {
      const base64File: string = URL.createObjectURL(file);
      setImageUrl(base64File);
    }
  };

  const formatListImg = () => {
    const listImg = [firstId, secondId, thirdId];
    const resultList = listImg.filter(element => element);
    return resultList;
  };

  const setDataProduct = () => {
    setImageUrl(productDataDetail?.url || '');
    setFileName(productDataDetail?.dataSheetName || productDataDetail?.dataSheet || '');
    setFileUrl(productDataDetail?.dataSheet || '');
    const lengthDetail = productDataDetail?.secondaryImages ? productDataDetail?.secondaryImages?.length : 0;
    setFirstImg(lengthDetail > 0 ? productDataDetail?.secondaryImages[0]?.url || '' : '');
    setFirstId(lengthDetail > 0 ? productDataDetail?.secondaryImages[0]?.id || null : null);
    setSecondImg(lengthDetail > 1 ? productDataDetail?.secondaryImages[1]?.url || '' : '');
    setSecondId(lengthDetail > 1 ? productDataDetail?.secondaryImages[1]?.id || null : null);
    setThirdImg(lengthDetail > 2 ? productDataDetail?.secondaryImages[2]?.url || '' : '');
    setThirdId(lengthDetail > 2 ? productDataDetail?.secondaryImages[2]?.id || null : null);
    setIsKit(!!productDataDetail?.isKit);
  };

  const formPricingDTO = (values: any) => {
    const valuesEntries = Object.entries(values);
    if (!countries || countries.length < 1) return;
    let countriesPricingData: { [key: string]: any } | undefined = undefined;
    valuesEntries.forEach(([key, value]) => {
      let currentDataBelongingCountry: string | undefined = undefined;
      const testIsCountryPricingInfo = countries.some((countryNameCode: string) => {
        if (key.startsWith(`${countryNameCode}-`)) {
          currentDataBelongingCountry = countryNameCode;
          return true;
        } else {
          currentDataBelongingCountry = undefined;
          return false;
        }
      });
      if (!testIsCountryPricingInfo || !currentDataBelongingCountry) return;
      const existingCountriesData = countriesPricingData;
      const propertyName = key.split('-')[1];
      const existingCountryData = countriesPricingData ? countriesPricingData[currentDataBelongingCountry] : undefined;
      if (existingCountryData) {
        const updatedCountryData = {
          ...existingCountryData,
          [`${propertyName}`]: propertyName === 'isActive' ? (value === true ? 1 : 0) : value
        };
        countriesPricingData = { ...existingCountriesData, [`${currentDataBelongingCountry}`]: updatedCountryData };
      } else {
        const completeCountryExistingData = countriesInfoObject ? countriesInfoObject[currentDataBelongingCountry] : {};
        countriesPricingData = {
          ...existingCountriesData,
          [`${currentDataBelongingCountry}`]: {
            ...completeCountryExistingData,
            [`${propertyName}`]: propertyName === 'isActive' ? (value === true ? 1 : 0) : value
          }
        };
      }
    });
    const pricingDataArr = countriesPricingData
      ? Object.entries(countriesPricingData).map(([key, val]: [string, any]) => {
          const hasCompletePricing = !!val?.providerPrice && !!val?.margin;
          return { ...val, nameCode: key, isActive: hasCompletePricing ? val?.isActive : 0 };
        })
      : [];

    // const finalPricingData = productDataDetail?.productId
    //   ? pricingDataArr.map(({ isActive, isQuoteProduct, ...rest }: any) => rest)
    //   : pricingDataArr;
    return pricingDataArr;
  };

  const onFinish = async (values: any) => {
    if (!productDataDetail?.isActive && productDataDetail?.productId) return;
    const updatedPricingData = formPricingDTO(values);
    const newCountryHasCompletePricing = !!values?.providerPrice && !!values?.margin;
    const pricingNewCountry = showPricingForm
      ? {
          availableProviders: values?.availableProviders,
          name: values?.countryData.name,
          nameCode: values?.countryData.code,
          flag: values?.countryData.flag,
          currencyCode: values?.countryData.currencyCode,
          currency: values?.countryData.currencyCode,
          taxRate: values?.taxRate,
          exchangeRate: values?.exchangeRate,
          providerPrice: values?.providerPrice,
          margin: values?.margin,
          nudosPriceUsd: values?.nudosPriceUsd,
          nudosPrice: values?.nudosPrice,
          nudosPriceUsdWithTaxes: values?.nudosPriceUsdWithTaxes,
          nudosPriceWithTaxes: values?.nudosPriceWithTaxes,
          providerId: values?.providerId,
          isActive: newCountryHasCompletePricing ? (values?.newCountryIsActive ?? true) : false,
          isQuoteProduct: values?.newCountryIsQuoteProduct ?? false,
          inStock: true
        }
      : null;
    const createPricingData = pricingNewCountry === null ? [] : [pricingNewCountry];

    const valuesNotPertainingPricing = Object.fromEntries(
      Object.entries(values).filter(([key]) => {
        return !countries?.some(countryCode => key.startsWith(`${countryCode}-`));
      })
    );

    const body: any = {
      ...productDataDetail,
      ...valuesNotPertainingPricing,
      brandId: values.brandId,
      color: values.color,
      commercialName: values.commercialName,
      description: values.description,
      displayInches: values.displayInches,
      hardDrive: values.hardDrive,
      inBoxAccessories: values.inBoxAccessories,
      positionId: values.positionId,
      processor: values.processor,
      profileIds: values.profileIds,
      ram: values.ram,
      software: values.software,
      warranty: values.warranty,
      productId: productDataDetail?.productId,
      imageId: imgId || productDataDetail?.imageId || 0,
      dataSheet: fileUrl,
      dataSheetName: fileName,
      secondaryImages: formatListImg(),
      pricingData: !productDataDetail?.productId
        ? createPricingData
        : showPricingForm
        ? [...(updatedPricingData as []), pricingNewCountry]
        : updatedPricingData,
      isKit: typeProduct.id === 6 ? isKit : false,
      canBeLoaded: !isKit
    };

    if (productDataDetail?.productId) {
      setPendingStoreStatusChanges([]);
      form.resetFields();     
      setSelectedProfiles([]);
      setShowPricingForm(false);
      await updateProduct(body, pendingStoreStatusChanges);
    } else {
      const dataProduct = await createProduct(body);
      if (dataProduct.status === 'success') {
        form.resetFields();
        setShowPricingForm(false);
        setSelectedProfiles([]);
        if (getDataOfTheCreatedProduct) getDataOfTheCreatedProduct(dataProduct?.productId);
      }
    }
  };
  const verifyValidations = (editProduct: boolean) => {
    if (editProduct) {
      if (
        verifyGeneralInfo &&
        verifySpecs &&
        verifyPricing &&
        (!!imgId || !!imageUrl) &&
        (updateInfo || changePricing)
      ) {
        return false;
      }
      return true;
    } else {
      if (verifyGeneralInfo && verifySpecs && verifyPricing && !!imgId) {
        return false;
      }
      return true;
    }
  };

  const verifyFileSheet = (file: File) => {
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isDocument = extension === '.docx' || extension === '.pdf';
    const size = file.size / 1024 / 1024 < 3;
    if (!isDocument) {
      displayErrorNotification({ customJSXMessage: <>solo puedes subir archivos .docx/.pdf</> });
      return true;
    } else if (!size) {
      displayErrorNotification({ customJSXMessage: <>el archivo debe pesar menos de 3MB</> });
    }
    return false;
  };
  const chargeFile = async (file: string | Blob | RcFile | File | any) => {
    const incorrectFile = verifyFileSheet(file);
    if (file && !incorrectFile) {
      setLoadingSheet(true);
      try {
        const fileSheet = await createURLFile(file);
        setFileUrl(fileSheet?.url || '');
        setFileName(fileSheet?.name || '');
        setLoadingSheet(false);
        setUpdateInfo(true);
      } catch (error) {
        setLoadingSheet(false);
        displayErrorNotification();
      }
    }
  };
  const chargeImg = async (file: string | Blob | RcFile) => {
    const checkFile = verifyFile(file, true);
    setLoading(true);
    if (file && !checkFile) {
      try {
        const dataImg = await createImage(file);
        setImgId(dataImg.data.catalogImageId);
        setLoading(false);
        setUpdateInfo(true);
      } catch (error) {
        setLoading(false);
        displayErrorNotification();
      }
    } else {
      setLoading(false);
    }
  };

  const chargeSecondaryFiles = async (position: string, file: any) => {
    const checkFile = verifyFile(file, true);
    if (file && !checkFile) {
      try {
        const secondaryImg = await createImage(file, true);
        setUpdateInfo(true);
        if (position === 'first') {
          setFirstImg(secondaryImg?.data?.medium);
          setFirstId(secondaryImg?.data?.catalogImageId);
          setFirstLoading(false);
        }
        if (position === 'secondary') {
          setSecondImg(secondaryImg?.data?.medium);
          setSecondId(secondaryImg?.data?.catalogImageId);
          setSecondLoading(false);
        }
        if (position === 'third') {
          setThirdImg(secondaryImg?.data?.medium);
          setThirdId(secondaryImg?.data?.catalogImageId);
          setThirdLoading(false);
        }
      } catch (error) {
        displayErrorNotification();
        setFirstLoading(false);
        setSecondLoading(false);
        setThirdLoading(false);
      }
    } else {
      setFirstLoading(false);
      setSecondLoading(false);
      setThirdLoading(false);
    }
  };

  const getAllCountries = async () => {
    try {
      const countries = await getCountriesOpenForSelling();
      if (countries) setCountriesOpenForSale(countries);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const deleteImg = (position: string) => {
    setUpdateInfo(true);
    if (position === 'first') {
      setFirstImg(undefined);
      setFirstId(null);
    }
    if (position === 'second') {
      setSecondImg(undefined);
      setSecondId(null);
    }
    if (position === 'third') {
      setThirdImg(undefined);
      setThirdId(null);
    }
  };

  const handleVisibleChange = (newVisible: boolean) => {
    setVisible(newVisible);
  };

  const verifySendToCatalog = () => {
    if (sendToCatalog) {
      push('/catalog/product-list');
    } else {
      goBack();
    }
  };

  const confirmReturnPopoverContent = (
    <div className="genericPopoverContent">
      <div className="textPopup">
        Se descartaran los cambios <br /> ¿Seguro que deseas regresar?
      </div>
      <Button className="buttonPoup margin" onClick={() => setVisible(false)}>
        No
      </Button>
      <Button className="buttonPoup" onClick={verifySendToCatalog}>
        Si
      </Button>
    </div>
  );

  const renderSwitchButton = (
    <div id="customSwitch">
      <Switch
        checked={productDataDetail?.productId ? productDataDetail?.isActive : true}
        onChange={() => {
          if (productDataDetail?.productId) {
            setShowConfirmModal(true);
          }
        }}
        checkedChildren="Activo en catálogo"
        unCheckedChildren="Inactivo en catálogo"
        style={{ height: '24px' }}
      />
    </div>
  );

  const isButtonDisabled = verifyValidations(!!productDataDetail || loadingProduct || loading || !imageUrl);

  const editIsKit = (isKit: boolean) => {
    setIsKit(isKit);
  };

  const handleActivateOrDeactivateProductsTrigger = () => {
    activateOrDeactivateProductsTrigger({
      isActivate: productDataDetail?.isActive as boolean,
      body: [productDataDetail?.productId as number]
    });
  };

  useEffect(() => {
    setDataProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDataDetail]);

  useEffect(() => {
    forceUpdate({});
    getAllCountries();
  }, []);

  return (
    <div id="formProduct" className="relative">
      {!productDataDetail?.isActive && productDataDetail?.productId && (
        <div className="backHeader">
          <div className="backIcon" onClick={() => goBack()}>
            <ArrowIcon fill="#070F21" />
          </div>
        </div>
      )}
      {showConfirmModal && (
        <ActiveOrInactiveConfirmModal
          firstButtonAction={() => setShowConfirmModal(false)}
          secondButtonAction={handleActivateOrDeactivateProductsTrigger}
          isLoading={isLoadingActivateOrDeactivateProducts}
          isSingleActiveProduct={productDataDetail?.isActive}
        />
      )}
      {!productDataDetail?.isActive && productDataDetail?.productId && (
        <div className="floatInactiveButton">
          <div className="customText font-bold">{type}</div>
          {renderSwitchButton}
        </div>
      )}
      {productDataDetail?.productId && <div className={`${productDataDetail?.isActive ? '' : 'disableContainer'}`} />}
      {(productDataDetail?.isActive || !productDataDetail?.productId) && (
        <div className="backHeader">
          <Popover
            trigger="click"
            placement="bottomRight"
            open={visible}
            onOpenChange={handleVisibleChange}
            content={confirmReturnPopoverContent}
          >
            <div className="backIcon">
              <ArrowIcon fill="#070F21" />
            </div>
          </Popover>
        </div>
      )}
      {loadingProduct ? (
        <DescriptionSkeleton />
      ) : (
        <Form
          name="register_product"
          onFinish={onFinish}
          form={form}
          onFieldsChange={() => {
            setUpdateInfo(true);
          }}
          disabled={!!productDataDetail?.productId && !productDataDetail?.isActive}
        >
          <div className="containerDescription">
            <div className="principalImg">
              {!loading ? (
                <Upload
                  customRequest={e => {
                    if (e.file) {
                      chargeImg(e.file);
                    }
                  }}
                  name="img"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                >
                  {imageUrl ? (
                    <div className="containerChargeImg">
                      <img className="positionImg" src={imageUrl} alt="description_product" />
                    </div>
                  ) : (
                    <UploadButton loading={loading} />
                  )}
                </Upload>
              ) : (
                <UploadButton loading={true} />
              )}
            </div>

            <div className="moreImages">
              <div className="containerMoreImg">
                <div className={`typeProduct relative ${renderMarginTop}`}>
                  {(productDataDetail?.isActive || !productDataDetail?.productId) && (
                    <div className="containerTitle">
                      <div className="customText">{type}</div>
                      {renderSwitchButton}
                    </div>
                  )}
                </div>
                <div className="productFirstLineContainer">
                  <div className="productNameContainer">
                    <div className="text-xs mb-2" style={{ lineHeight: '14px', color: '#B3B3B3' }}>
                      Nombre comercial
                    </div>
                    <div id="customInput">
                      <Form.Item
                        initialValue={productDataDetail?.commercialName}
                        name="commercialName"
                        rules={[{ required: true, message: 'campo requerido' }]}
                      >
                        <Input placeholder="Ej." />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="skuProductContainer">
                    <NudosTextInput
                      isDeactivated
                      isFilled
                      placeholder="Código único"
                      label="SKU"
                      componentSize="medium"
                      styleLabel="styleLabel"
                      defaultValue={productDataDetail?.uid || ''}
                    />
                  </div>
                </div>
                <div className="containerFiles">
                  <>
                    <MinUpLoadImg
                      imgUrl={firstImg}
                      loading={firsLoading}
                      diff={'first'}
                      deleteImg={() => {
                        deleteImg('first');
                      }}
                      changeFile={e => {
                        setFirstLoading(true);
                        chargeSecondaryFiles('first', e);
                      }}
                    />
                    <MinUpLoadImg
                      imgUrl={secondImg}
                      loading={secondLoading}
                      diff={'secondary'}
                      deleteImg={() => {
                        deleteImg('second');
                      }}
                      changeFile={e => {
                        setSecondLoading(true);
                        chargeSecondaryFiles('secondary', e);
                      }}
                    />
                    <MinUpLoadImg
                      imgUrl={thirdImg}
                      loading={thirdLoading}
                      diff={'third'}
                      deleteImg={() => {
                        deleteImg('third');
                      }}
                      changeFile={e => {
                        setThirdLoading(true);
                        chargeSecondaryFiles('third', e);
                      }}
                    />

                    {fileUrl && fileName ? (
                      <div className="fileCharged">
                        <div className="textCharged">{truncateText(fileName, 25)}</div>
                        <DeleteFile
                          width={20}
                          height={20}
                          className="positionIconDeleteFile"
                          onClick={() => {
                            setFileUrl('');
                            setFileName('');
                            setUpdateInfo(true);
                          }}
                        />
                      </div>
                    ) : (
                      <Upload
                        name="fileTech"
                        customRequest={e => {
                          if (e.file) {
                            chargeFile(e.file);
                          }
                        }}
                        showUploadList={false}
                      >
                        <div className="uploadFileContainer">
                          <>
                            {loadingSheet ? (
                              <LoadingOutlined className="loaderFile" />
                            ) : (
                              <>
                                <div className="textFile">
                                  Ficha tecnica
                                  <br />
                                  (opcional)
                                </div>
                                <img className="sizeIconFile" alt="fileIcon" src={fileIcon} />
                              </>
                            )}
                          </>
                        </div>
                      </Upload>
                    )}
                  </>
                </div>
              </div>
            </div>
          </div>
          <Tabs
            productDataDetail={productDataDetail}
            setProductDataDetail={setProductDataDetail}
            typeProduct={typeProduct}
            changeTab={changeTab}
            selectTab={selectTab}
            setVerifyGeneralInfo={setVerifyGeneralInfo}
            setVerifySpecs={setVerifySpecs}
            setVerifyPricing={setVerifyPricing}
            setChangePricing={setChangePricing}
            countriesOpenForSale={countriesOpenForSale}
            setUpdateInfo={setUpdateInfo}
            editIsKit={editIsKit}
            isKit={isKit}
            selectedProfiles={selectedProfiles}
            setSelectedProfiles={setSelectedProfiles}
            addPendingStoreStatusChange={addPendingStoreStatusChange}
          />
          <FloatButtons>
            <Form.Item className="positionButton" shouldUpdate>
              {() => (
                <Button type="primary" htmlType="submit" className="grayButton" disabled={isButtonDisabled}>
                  <div className="litleButton">
                    <EditIcon
                      className="positionIcon"
                      stroke={isButtonDisabled ? '#B3B3B3' : ''}
                      fill={isButtonDisabled ? '#ececec' : ''}
                    />
                  </div>
                  Guardar
                </Button>
              )}
            </Form.Item>
          </FloatButtons>
        </Form>
      )}
    </div>
  );
};
export default ProductForm;
